import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  FiShoppingCart, FiDownload, FiPrinter, FiRotateCcw, FiCheckCircle,
  FiFileText, FiPackage, FiArrowUp, FiArrowDown, FiClock,
  FiChevronDown, FiChevronRight, FiAlertCircle, FiDollarSign, FiExternalLink
} from 'react-icons/fi';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line
} from 'recharts';
import { colors } from '../../theme/colors';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import PageLayout from '../../components/PageLayout';
import VendorSelect from '../../components/VendorSelect';
import { formatCurrency, formatNumber, formatDate } from '../../utils/formatters';
import { navigateToTransaction } from '../../utils/navigationHelper';
import useReportMemory from '../../hooks/useReportMemory';
import { addPrintBrand } from '../../utils/printBranding';

// ── Constants ──
const CHART_COLORS = ['#800020', '#A0153E', '#5D0017', '#C4A484', '#8B7355', '#D4AF37'];

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'Draft', label: 'Draft' },
  { value: 'Approved', label: 'Approved' },
  { value: 'Partially Received', label: 'Partially Received' },
  { value: 'Completed', label: 'Completed' },
  { value: 'Closed', label: 'Closed' },
  { value: 'Cancelled', label: 'Cancelled' }
];

const FULFILLMENT_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'open', label: 'Open (0%)' },
  { value: 'partial', label: 'Partially Fulfilled' },
  { value: 'full', label: 'Fully Fulfilled' }
];

const STATUS_COLORS = {
  Draft: colors.neutral[500],
  Approved: colors.info.main,
  'Partially Received': colors.warning.main,
  Completed: colors.success.main,
  Closed: '#546E7A',
  Cancelled: colors.error.main
};

const PIE_STATUS_COLORS = [
  colors.neutral[500],   // Draft
  colors.info.main,      // Approved
  colors.warning.main,   // Partially Received
  colors.success.main,   // Completed
  '#546E7A',             // Closed
  colors.error.main      // Cancelled
];

// ── Helpers ──

const getMonthKey = (dateStr) => {
  if (!dateStr) return 'Unknown';
  const d = new Date(dateStr);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const getMonthLabel = (key) => {
  if (!key || key === 'Unknown') return 'Unknown';
  const [y, m] = key.split('-');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[parseInt(m, 10) - 1]} ${y}`;
};

const formatAmountLocal = (amount) => {
  const dp = (typeof window !== 'undefined' && Number.isFinite(Number(window.__AMAL_DECIMAL_PLACES__)))
    ? Number(window.__AMAL_DECIMAL_PLACES__) : 2;
  if (!amount && amount !== 0) return dp > 0 ? '0.' + '0'.repeat(dp) : '0';
  return Number(amount).toLocaleString('en-PK', {
    minimumFractionDigits: dp,
    maximumFractionDigits: dp
  });
};

// ── Component ──

const POTracking = () => {
  const { activeCompany, activeFinancialYear, activeBranch } = useAuth();
  const [savedMem, saveMemory] = useReportMemory('po-tracking');
  const currencyCode = activeCompany?.currencyCode || 'PKR';
  const currencySymbol = activeCompany?.currencySymbol || 'Rs.';
  const navigate = useNavigate();

  // Data states
  const [poTransactions, setPOTransactions] = useState(() => savedMem?.poTransactions ?? []);
  const [childTransactions, setChildTransactions] = useState(() => savedMem?.childTransactions ?? []);
  const [vendors, setVendors] = useState([]);
  const [financialYears, setFinancialYears] = useState([]);
  const [loading, setLoading] = useState(savedMem ? false : true);
  const [expandedRows, setExpandedRows] = useState(() => savedMem?.expandedRows ?? {});

  // Filter states
  const [selectedFYId, setSelectedFYId] = useState(() => savedMem?.selectedFYId ?? '');
  const [dateFrom, setDateFrom] = useState(() => savedMem?.dateFrom ?? '');
  const [dateTo, setDateTo] = useState(() => savedMem?.dateTo ?? '');
  const [selectedVendor, setSelectedVendor] = useState(() => savedMem?.selectedVendor ?? 'all');
  const [selectedStatus, setSelectedStatus] = useState(() => savedMem?.selectedStatus ?? 'all');
  const [selectedFulfillment, setSelectedFulfillment] = useState(() => savedMem?.selectedFulfillment ?? 'all');

  // Sort states
  const [sortColumn, setSortColumn] = useState(() => savedMem?.sortColumn ?? 'docDate');
  const [sortDirection, setSortDirection] = useState(() => savedMem?.sortDirection ?? 'desc');

  // ── Load Financial Years ──
  const loadFinancialYears = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const fyRef = collection(db, 'companies', activeCompany.id, 'financialYears');
      const q = query(fyRef, orderBy('startDate', 'desc'));
      const snapshot = await getDocs(q);
      const fys = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setFinancialYears(fys);

      if (!savedMem) {
        if (activeFinancialYear?.id) {
          setSelectedFYId(activeFinancialYear.id);
        } else if (fys.length > 0) {
          setSelectedFYId(fys[0].id);
        }
      }
    } catch (error) {
      console.error('Error loading financial years:', error);
    }
  }, [activeCompany, activeFinancialYear, savedMem]);

  // ── Load Vendors ──
  const loadVendors = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const ref = collection(db, 'companies', activeCompany.id, 'vendors');
      const q = query(ref, orderBy('vendorName'));
      const snapshot = await getDocs(q);
      setVendors(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (error) {
      console.error('Error loading vendors:', error);
    }
  }, [activeCompany]);

  // ── Load PO Transactions ──
  const loadTransactions = useCallback(async () => {
    if (!activeCompany?.id || !selectedFYId) return;
    try {
      setLoading(true);
      const ref = collection(db, 'companies', activeCompany.id, 'transactions');

      // Load POs
      const poQuery = query(ref,
        where('tranTypeCode', '==', 'PO'),
        where('financialYearId', '==', selectedFYId)
      );
      const poSnapshot = await getDocs(poQuery);
      const poData = poSnapshot.docs.map(d => ({ id: d.id, ...d.data() }))
        .filter(t => {
          if (!activeBranch || activeBranch.id === 'all') return true;
          return t.branchId === activeBranch.id;
        });
      poData.sort((a, b) => (b.docDate || '').localeCompare(a.docDate || ''));
      setPOTransactions(poData);

      // Load child GRNs and PIs that reference these POs
      const poIds = poData.map(p => p.id);
      let childData = [];

      if (poIds.length > 0) {
        // Firestore 'in' operator supports max 30 values per query
        const chunks = [];
        for (let i = 0; i < poIds.length; i += 30) {
          chunks.push(poIds.slice(i, i + 30));
        }

        for (const chunk of chunks) {
          const grnQuery = query(ref,
            where('tranTypeCode', '==', 'GRN'),
            where('parentId', 'in', chunk)
          );
          const piQuery = query(ref,
            where('tranTypeCode', 'in', ['PI', 'NPI']),
            where('parentId', 'in', chunk)
          );

          const [grnSnapshot, piSnapshot] = await Promise.all([
            getDocs(grnQuery),
            getDocs(piQuery)
          ]);

          grnSnapshot.docs.forEach(d => childData.push({ id: d.id, ...d.data() }));
          piSnapshot.docs.forEach(d => childData.push({ id: d.id, ...d.data() }));
        }
      }

      setChildTransactions(childData.filter(t => {
        if (!activeBranch || activeBranch.id === 'all') return true;
        return t.branchId === activeBranch.id;
      }));
    } catch (error) {
      console.error('Error loading PO transactions:', error);
    } finally {
      setLoading(false);
    }
  }, [activeCompany, selectedFYId, activeBranch]);

  useEffect(() => { loadFinancialYears(); }, [loadFinancialYears]);
  useEffect(() => { loadVendors(); }, [loadVendors]);
  const skipInitialLoad = useRef(!!savedMem);
  useEffect(() => {
    if (skipInitialLoad.current) { skipInitialLoad.current = false; return; }
    if (selectedFYId) loadTransactions();
  }, [loadTransactions, selectedFYId]);

  // ── Backward memory: persist filters + generated rows (auto-load, display-only) ──
  useEffect(() => {
    if (loading) return;
    saveMemory({
      selectedFYId, dateFrom, dateTo, selectedVendor, selectedStatus, selectedFulfillment,
      sortColumn, sortDirection,
      poTransactions, childTransactions, expandedRows
    });
  }, [loading, selectedFYId, dateFrom, dateTo, selectedVendor, selectedStatus, selectedFulfillment,
      sortColumn, sortDirection, poTransactions, childTransactions, expandedRows, saveMemory]);

  // ── Build enriched PO data with child info ──
  const enrichedPOs = useMemo(() => {
    const grnsByParent = {};
    const pisByParent = {};

    childTransactions.forEach(child => {
      if (!child.parentId) return;
      if (child.tranTypeCode === 'GRN') {
        if (!grnsByParent[child.parentId]) grnsByParent[child.parentId] = [];
        grnsByParent[child.parentId].push(child);
      } else if (['PI', 'NPI'].includes(child.tranTypeCode)) {
        if (!pisByParent[child.parentId]) pisByParent[child.parentId] = [];
        pisByParent[child.parentId].push(child);
      }
    });

    return poTransactions.map(po => {
      const grns = grnsByParent[po.id] || [];
      const pis = pisByParent[po.id] || [];

      // Calculate total PO qty from lines
      const poTotalQty = (po.lines || []).reduce((sum, line) => sum + (line.quantity || 0), 0);

      // Build combined receivings list (GRNs + PIs are both "receivings" against a PO)
      const receivings = [
        ...grns.map(grn => {
          const qty = (grn.lines || []).reduce((s, l) => s + (l.acceptedQty || l.receivedQty || l.quantity || 0), 0);
          return { ...grn, type: 'GRN', qty, amount: grn.netAmount || grn.totalAmount || 0 };
        }),
        ...pis.map(pi => {
          const qty = (pi.lines || []).reduce((s, l) => s + (l.quantity || 0), 0);
          return { ...pi, type: 'PI', qty, amount: pi.netAmount || pi.totalAmount || 0 };
        })
      ].sort((a, b) => (a.docDate || '').localeCompare(b.docDate || ''));

      const receivingCount = receivings.length;
      // BUG FIX C3: Use max(GRN total qty, PI total qty) instead of sum to avoid
      // double-counting when both a GRN and a direct PI exist for the same PO.
      // A PI invoices the same goods a GRN already received, so they overlap.
      const grnTotalQty = receivings.filter(r => r.type === 'GRN').reduce((s, r) => s + (r.qty || 0), 0);
      const piTotalQty = receivings.filter(r => r.type === 'PI').reduce((s, r) => s + (r.qty || 0), 0);
      const receivingTotalQty = Math.max(grnTotalQty, piTotalQty);
      const receivingTotalAmount = receivings.reduce((s, r) => s + (r.amount || 0), 0);

      // Build item-wise tracking: aggregate PO qty, received qty per item
      // BUG FIX C3: Track GRN and PI received qty separately per item, then use
      // Math.max() to avoid double-counting (PI invoices the same goods GRN received)
      const itemMap = {};
      (po.lines || []).forEach(line => {
        const key = line.itemId || line.description || `line-${line.lineNumber}`;
        itemMap[key] = {
          itemId: line.itemId || '',
          itemCode: line.itemCode || '',
          itemName: line.itemName || line.description || '',
          uom: line.unitOfMeasure || '',
          orderedQty: (line.quantity || 0),
          grnReceivedQty: 0,
          piReceivedQty: 0,
          unitPrice: line.unitPrice || 0,
          orderedAmount: line.lineTotal || ((line.quantity || 0) * (line.unitPrice || 0))
        };
      });

      // GRN received qty
      grns.forEach(grn => {
        (grn.lines || []).forEach(line => {
          const key = line.itemId || line.description || `line-${line.lineNumber}`;
          if (itemMap[key]) {
            itemMap[key].grnReceivedQty += (line.acceptedQty || line.receivedQty || line.quantity || 0);
          } else {
            itemMap[key] = {
              itemId: line.itemId || '', itemCode: line.itemCode || '',
              itemName: line.itemName || line.description || '', uom: line.unitOfMeasure || '',
              orderedQty: 0, grnReceivedQty: (line.acceptedQty || line.receivedQty || line.quantity || 0),
              piReceivedQty: 0,
              unitPrice: line.unitCost || 0, orderedAmount: 0
            };
          }
        });
      });

      // PI received qty (tracked separately)
      pis.forEach(pi => {
        (pi.lines || []).forEach(line => {
          const key = line.itemId || line.description || `line-${line.lineNumber}`;
          if (itemMap[key]) {
            itemMap[key].piReceivedQty += (line.quantity || 0);
          } else {
            itemMap[key] = {
              itemId: line.itemId || '', itemCode: line.itemCode || '',
              itemName: line.itemName || line.description || '', uom: line.unitOfMeasure || '',
              orderedQty: 0, grnReceivedQty: 0,
              piReceivedQty: (line.quantity || 0),
              unitPrice: line.unitPrice || 0, orderedAmount: 0
            };
          }
        });
      });

      // A Purchase Order closed by hand: what was left is closed, not pending.
      const poClosed = po.status === 'Closed';
      const itemBreakdown = Object.values(itemMap).map(item => {
        // Use max of GRN and PI qty to avoid double-counting (BUG FIX C3)
        const receivedQty = Math.max(item.grnReceivedQty, item.piReceivedQty);
        return {
          ...item,
          receivedQty,
          pendingQty: poClosed ? 0 : Math.max(0, item.orderedQty - receivedQty),
          closedQty: poClosed ? Math.max(0, item.orderedQty - receivedQty) : 0,
          receivedPct: item.orderedQty > 0 ? Math.min((receivedQty / item.orderedQty) * 100, 100) : 0
        };
      });

      const poAmount = po.netAmount || po.totalAmount || 0;
      const fulfillmentPct = poTotalQty > 0 ? Math.min((receivingTotalQty / poTotalQty) * 100, 100) : 0;

      return {
        ...po,
        receivings,
        receivingCount,
        poTotalQty,
        receivingTotalQty,
        receivingTotalAmount,
        poAmount,
        fulfillmentPct,
        itemBreakdown
      };
    });
  }, [poTransactions, childTransactions]);

  // ── Filtered Data ──
  const filteredData = useMemo(() => {
    let data = [...enrichedPOs];

    if (dateFrom) {
      data = data.filter(t => (t.docDate || '') >= dateFrom);
    }
    if (dateTo) {
      data = data.filter(t => (t.docDate || '') <= dateTo);
    }
    if (selectedVendor !== 'all') {
      data = data.filter(t => t.partyId === selectedVendor);
    }
    if (selectedStatus !== 'all') {
      data = data.filter(t => t.status === selectedStatus);
    }
    if (selectedFulfillment !== 'all') {
      switch (selectedFulfillment) {
        case 'open':
          data = data.filter(t => t.fulfillmentPct === 0 && t.status !== 'Closed');
          break;
        case 'partial':
          data = data.filter(t => t.fulfillmentPct > 0 && t.fulfillmentPct < 100);
          break;
        case 'full':
          data = data.filter(t => t.fulfillmentPct >= 100);
          break;
        default:
          break;
      }
    }

    return data;
  }, [enrichedPOs, dateFrom, dateTo, selectedVendor, selectedStatus, selectedFulfillment]);

  // ── Summary Cards ──
  const summary = useMemo(() => {
    const totalPOs = filteredData.length;
    const openPOs = filteredData.filter(p => p.status === 'Approved' || p.status === 'Draft').length;
    const partiallyReceived = filteredData.filter(p => p.status === 'Partially Received').length;
    const completed = filteredData.filter(p => p.status === 'Completed').length;
    const totalValue = filteredData.reduce((sum, p) => sum + (p.poAmount || 0), 0);

    return { totalPOs, openPOs, partiallyReceived, completed, totalValue };
  }, [filteredData]);

  // ── Chart Data: PO Status Distribution (Pie) ──
  const statusPieData = useMemo(() => {
    const statusMap = {};
    filteredData.forEach(po => {
      const s = po.status || 'Unknown';
      if (!statusMap[s]) statusMap[s] = { name: s, value: 0 };
      statusMap[s].value += 1;
    });
    const order = ['Draft', 'Approved', 'Partially Received', 'Completed', 'Closed', 'Cancelled'];
    return order
      .filter(s => statusMap[s])
      .map(s => statusMap[s]);
  }, [filteredData]);

  // ── Chart Data: PO Value by Vendor (Top 10 Bar) ──
  const vendorBarData = useMemo(() => {
    const vendorMap = {};
    filteredData.forEach(po => {
      const name = po.partyName || 'Unknown';
      if (!vendorMap[name]) vendorMap[name] = { vendor: name, value: 0 };
      vendorMap[name].value += (po.poAmount || 0);
    });
    return Object.values(vendorMap)
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);
  }, [filteredData]);

  // ── Chart Data: PO Trend Over Time (Line) ──
  const trendLineData = useMemo(() => {
    const monthMap = {};
    filteredData.forEach(po => {
      const key = getMonthKey(po.docDate);
      if (!monthMap[key]) monthMap[key] = { month: key, count: 0, value: 0 };
      monthMap[key].count += 1;
      monthMap[key].value += (po.poAmount || 0);
    });
    return Object.values(monthMap)
      .sort((a, b) => a.month.localeCompare(b.month))
      .map(m => ({
        ...m,
        label: getMonthLabel(m.month)
      }));
  }, [filteredData]);

  // ── Sorted Table Data ──
  const sortedData = useMemo(() => {
    const data = [...filteredData];
    data.sort((a, b) => {
      let valA, valB;
      switch (sortColumn) {
        case 'docDate':
          valA = a.docDate || '';
          valB = b.docDate || '';
          break;
        case 'docNumber':
          valA = a.docNumber || '';
          valB = b.docNumber || '';
          break;
        case 'partyName':
          valA = (a.partyName || '').toLowerCase();
          valB = (b.partyName || '').toLowerCase();
          break;
        case 'poTotalQty':
          valA = a.poTotalQty || 0;
          valB = b.poTotalQty || 0;
          break;
        case 'poAmount':
          valA = a.poAmount || 0;
          valB = b.poAmount || 0;
          break;
        case 'receivingCount':
          valA = a.receivingCount || 0;
          valB = b.receivingCount || 0;
          break;
        case 'receivingTotalQty':
          valA = a.receivingTotalQty || 0;
          valB = b.receivingTotalQty || 0;
          break;
        case 'receivingTotalAmount':
          valA = a.receivingTotalAmount || 0;
          valB = b.receivingTotalAmount || 0;
          break;
        case 'fulfillmentPct':
          valA = a.fulfillmentPct || 0;
          valB = b.fulfillmentPct || 0;
          break;
        case 'status':
          valA = a.status || '';
          valB = b.status || '';
          break;
        default:
          valA = a.docDate || '';
          valB = b.docDate || '';
      }
      if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
      if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return data;
  }, [filteredData, sortColumn, sortDirection]);

  // ── Table Totals ──
  const tableTotals = useMemo(() => {
    return {
      poTotalQty: sortedData.reduce((s, p) => s + (p.poTotalQty || 0), 0),
      poAmount: sortedData.reduce((s, p) => s + (p.poAmount || 0), 0),
      receivingCount: sortedData.reduce((s, p) => s + (p.receivingCount || 0), 0),
      receivingTotalQty: sortedData.reduce((s, p) => s + (p.receivingTotalQty || 0), 0),
      receivingTotalAmount: sortedData.reduce((s, p) => s + (p.receivingTotalAmount || 0), 0)
    };
  }, [sortedData]);

  // ── Handlers ──
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const renderSortIcon = (column) => {
    if (sortColumn !== column) return null;
    return sortDirection === 'asc'
      ? <FiArrowUp size={11} style={{ marginLeft: 4 }} />
      : <FiArrowDown size={11} style={{ marginLeft: 4 }} />;
  };

  const toggleRowExpand = (poId) => {
    setExpandedRows(prev => ({
      ...prev,
      [poId]: !prev[poId]
    }));
  };

  const handleNavigateToPO = (poId) => {
    navigate('/purchases/purchase-order', { state: { viewTransactionId: poId } });
  };

  const handleResetFilters = () => {
    setDateFrom('');
    setDateTo('');
    setSelectedVendor('all');
    setSelectedStatus('all');
    setSelectedFulfillment('all');
    if (activeFinancialYear?.id) {
      setSelectedFYId(activeFinancialYear.id);
    }
  };

  // ── CSV Export ──
  const handleExport = () => {
    const selectedFY = financialYears.find(f => f.id === selectedFYId);
    let csv = 'PO Tracking Report\n';
    csv += `Company: ${activeCompany?.companyName || ''}\n`;
    csv += `Financial Year: ${selectedFY?.yearName || selectedFY?.yearCode || 'All'}\n`;
    csv += `Date Range: ${dateFrom || 'Start'} to ${dateTo || 'End'}\n`;
    csv += `Status: ${selectedStatus === 'all' ? 'All' : selectedStatus}\n`;
    csv += `Fulfillment: ${selectedFulfillment === 'all' ? 'All' : FULFILLMENT_OPTIONS.find(f => f.value === selectedFulfillment)?.label || selectedFulfillment}\n\n`;

    csv += 'Date,PO #,Vendor,Ordered Qty,PO Amount,Receivings,Received Qty,Received Amt,Fulfillment %,Status\n';

    sortedData.forEach(po => {
      csv += `"${formatDate(po.docDate)}",`;
      csv += `"${po.docNumber || ''}",`;
      csv += `"${po.partyName || ''}",`;
      csv += `${po.poTotalQty || 0},`;
      csv += `${(po.poAmount || 0).toFixed(2)},`;
      csv += `${po.receivingCount},`;
      csv += `${po.receivingTotalQty || 0},`;
      csv += `${(po.receivingTotalAmount || 0).toFixed(2)},`;
      csv += `${(po.fulfillmentPct || 0).toFixed(1)}%,`;
      csv += `"${po.status || ''}"\n`;

      // Item-wise rows
      if (po.itemBreakdown && po.itemBreakdown.length > 0) {
        po.itemBreakdown.forEach(item => {
          csv += `,,  ${item.itemCode || ''} - ${item.itemName || ''},`;
          csv += `${item.orderedQty},,,${item.receivedQty},,`;
          csv += `${item.receivedPct.toFixed(0)}%,${item.pendingQty > 0 ? item.pendingQty + ' pending' : item.closedQty > 0 ? item.closedQty + ' closed' : 'Done'}\n`;
        });
      }
    });

    csv += `\nTotals,,,${tableTotals.poTotalQty},"${tableTotals.poAmount.toFixed(2)}",${tableTotals.receivingCount},${tableTotals.receivingTotalQty},"${tableTotals.receivingTotalAmount.toFixed(2)}",,\n`;

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `PO_Tracking_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // ── Print ──
  const handlePrint = () => {
    const selectedFY = financialYears.find(f => f.id === selectedFYId);
    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>PO Tracking Report</title>
      <style>
        body{font-family:'Segoe UI',sans-serif;margin:30px;color:#1A1A1A;font-size:13px}
        h1{color:#800020;font-size:20px;margin-bottom:5px}
        .meta{color:#666;font-size:12px;margin-bottom:20px}
        .summary-row{display:flex;gap:20px;margin-bottom:20px;flex-wrap:wrap}
        .summary-box{border:1px solid #ddd;border-radius:6px;padding:10px 16px;min-width:140px}
        .summary-box .label{font-size:11px;color:#666;text-transform:uppercase}
        .summary-box .value{font-size:18px;font-weight:600;color:#1A1A1A}
        .summary-box .sub{font-size:10px;color:#999;margin-top:2px}
        table{width:100%;border-collapse:collapse;margin-top:10px}
        th{background:#800020;color:white;padding:8px 10px;text-align:left;font-size:11px;text-transform:uppercase}
        th.right{text-align:right}
        th.center{text-align:center}
        td{padding:6px 10px;border-bottom:1px solid #eee;font-size:12px}
        td.right{text-align:right;font-family:'Consolas',monospace}
        td.center{text-align:center}
        .totals-row{background:#f5f5f5;font-weight:700}
        .totals-row td{border-top:2px solid #800020;padding:10px}
        .status-badge{display:inline-block;padding:2px 8px;border-radius:4px;font-size:10px;font-weight:600}
        .progress-bar{width:80px;height:8px;background:#eee;border-radius:4px;overflow:hidden;display:inline-block;vertical-align:middle;margin-right:6px}
        .progress-fill{height:100%;border-radius:4px}
        @media print{body{margin:15px}}
      </style></head><body>
      <h1>PO Tracking Report</h1>
      <div class="meta">
        ${activeCompany?.companyName || 'Company'} |
        Financial Year: ${selectedFY?.yearName || selectedFY?.yearCode || 'All'} |
        Date Range: ${dateFrom || 'Start'} to ${dateTo || 'End'} |
        Status: ${selectedStatus === 'all' ? 'All' : selectedStatus}
      </div>
      <div class="summary-row">
        <div class="summary-box"><div class="label">Total POs</div><div class="value">${formatNumber(summary.totalPOs)}</div></div>
        <div class="summary-box"><div class="label">Open POs</div><div class="value">${formatNumber(summary.openPOs)}</div></div>
        <div class="summary-box"><div class="label">Partially Received</div><div class="value">${formatNumber(summary.partiallyReceived)}</div></div>
        <div class="summary-box"><div class="label">Completed</div><div class="value">${formatNumber(summary.completed)}</div></div>
        <div class="summary-box"><div class="label">Total PO Value</div><div class="value">${currencySymbol}${formatAmountLocal(summary.totalValue)}</div></div>
      </div>
      <table>
        <thead><tr>
          <th>Date</th><th>PO #</th><th>Vendor</th>
          <th class="right">Ord Qty</th><th class="right">PO Amount</th>
          <th class="center">Receivings</th><th class="right">Rcvd Qty</th><th class="right">Rcvd Amt</th>
          <th class="center">Fulfillment</th><th>Status</th>
        </tr></thead>
        <tbody>`);

    sortedData.forEach(po => {
      const pct = (po.fulfillmentPct || 0).toFixed(1);
      const barColor = po.fulfillmentPct >= 100 ? '#28A745' : po.fulfillmentPct > 0 ? '#FFC107' : '#DC3545';
      w.document.write(`
        <tr>
          <td>${formatDate(po.docDate)}</td>
          <td><strong>${po.docNumber || '-'}</strong></td>
          <td>${po.partyName || '-'}</td>
          <td class="right">${po.poTotalQty || 0}</td>
          <td class="right">${formatAmountLocal(po.poAmount)}</td>
          <td class="center">${po.receivingCount}</td>
          <td class="right">${po.receivingTotalQty || 0}</td>
          <td class="right">${formatAmountLocal(po.receivingTotalAmount)}</td>
          <td class="center">
            <div class="progress-bar"><div class="progress-fill" style="width:${pct}%;background:${barColor}"></div></div>
            ${pct}%
          </td>
          <td>${po.status || '-'}</td>
        </tr>`);

      // Item-wise detail rows
      if (po.itemBreakdown && po.itemBreakdown.length > 0) {
        po.itemBreakdown.forEach(item => {
          const iPct = item.receivedPct.toFixed(0);
          const iColor = item.receivedPct >= 100 ? '#28A745' : item.receivedPct > 0 ? '#FFC107' : '#DC3545';
          w.document.write(`
          <tr style="background:#f9f9f9;font-size:11px;color:#555">
            <td></td><td></td>
            <td style="padding-left:20px">${item.itemCode ? item.itemCode + ' - ' : ''}${item.itemName || '-'} ${item.uom ? '(' + item.uom + ')' : ''}</td>
            <td class="right">${item.orderedQty}</td><td></td>
            <td></td><td class="right" style="color:${item.receivedQty > 0 ? '#28A745' : ''}">${item.receivedQty}</td><td></td>
            <td class="center"><div class="progress-bar" style="width:50px"><div class="progress-fill" style="width:${iPct}%;background:${iColor}"></div></div> ${iPct}%</td>
            <td style="color:${item.pendingQty > 0 ? '#DC3545' : item.closedQty > 0 ? '#546E7A' : '#28A745'}">${item.pendingQty > 0 ? item.pendingQty + ' pending' : item.closedQty > 0 ? item.closedQty + ' closed' : 'Done'}</td>
          </tr>`);
        });
      }
    });

    w.document.write(`
        <tr class="totals-row">
          <td colspan="3" style="text-align:right;font-weight:700">TOTAL (${sortedData.length} POs)</td>
          <td class="right">${tableTotals.poTotalQty}</td>
          <td class="right">${formatAmountLocal(tableTotals.poAmount)}</td>
          <td class="center">${tableTotals.receivingCount}</td>
          <td class="right">${tableTotals.receivingTotalQty}</td>
          <td class="right">${formatAmountLocal(tableTotals.receivingTotalAmount)}</td>
          <td colspan="2"></td>
        </tr>
        </tbody></table>
        <div style="margin-top:30px;text-align:center;color:#999;font-size:11px">
          Generated on ${new Date().toLocaleString()} | AMAL ERP
        </div>
      </body></html>`);
    addPrintBrand(w);
    w.document.close();
    setTimeout(() => w.print(), 500);
  };

  // ── Custom Tooltip for Charts ──
  const CurrencyTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    return (
      <ChartTooltipWrapper>
        <ChartTooltipLabel>{label}</ChartTooltipLabel>
        {payload.map((entry, i) => (
          <ChartTooltipRow key={i}>
            <ChartTooltipDot style={{ background: entry.color }} />
            <span>{entry.name}:</span>
            <strong>{formatCurrency(entry.value)}</strong>
          </ChartTooltipRow>
        ))}
      </ChartTooltipWrapper>
    );
  };

  const PieTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    const entry = payload[0];
    return (
      <ChartTooltipWrapper>
        <ChartTooltipRow>
          <ChartTooltipDot style={{ background: entry.payload.fill || entry.color }} />
          <span>{entry.name}:</span>
          <strong>{formatNumber(entry.value)}</strong>
        </ChartTooltipRow>
      </ChartTooltipWrapper>
    );
  };

  const TrendTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    return (
      <ChartTooltipWrapper>
        <ChartTooltipLabel>{label}</ChartTooltipLabel>
        {payload.map((entry, i) => (
          <ChartTooltipRow key={i}>
            <ChartTooltipDot style={{ background: entry.color }} />
            <span>{entry.name}:</span>
            <strong>
              {entry.dataKey === 'count' ? formatNumber(entry.value) : formatCurrency(entry.value)}
            </strong>
          </ChartTooltipRow>
        ))}
      </ChartTooltipWrapper>
    );
  };

  // ── Fulfillment bar color ──
  const getFulfillmentColor = (pct) => {
    if (pct >= 100) return colors.success.main;
    if (pct > 0) return colors.warning.main;
    return colors.error.main;
  };

  // ── Render ──
  return (
    <PageLayout pageTitle="PO Tracking" breadcrumb="Reports / Tracking / PO Tracking">
      {/* Summary Cards */}
      <CardsGrid>
        <SummaryCard>
          <CardIcon color={CHART_COLORS[0]}>
            <FiShoppingCart />
          </CardIcon>
          <CardInfo>
            <CardLabel>Total POs</CardLabel>
            <CardValue>{formatNumber(summary.totalPOs)}</CardValue>
          </CardInfo>
        </SummaryCard>

        <SummaryCard>
          <CardIcon color={colors.info.main}>
            <FiClock />
          </CardIcon>
          <CardInfo>
            <CardLabel>Open POs</CardLabel>
            <CardValue>{formatNumber(summary.openPOs)}</CardValue>
          </CardInfo>
        </SummaryCard>

        <SummaryCard>
          <CardIcon color={colors.warning.main}>
            <FiPackage />
          </CardIcon>
          <CardInfo>
            <CardLabel>Partially Received</CardLabel>
            <CardValue>{formatNumber(summary.partiallyReceived)}</CardValue>
          </CardInfo>
        </SummaryCard>

        <SummaryCard>
          <CardIcon color={colors.success.main}>
            <FiCheckCircle />
          </CardIcon>
          <CardInfo>
            <CardLabel>Completed</CardLabel>
            <CardValue>{formatNumber(summary.completed)}</CardValue>
          </CardInfo>
        </SummaryCard>

        <SummaryCard>
          <CardIcon color={CHART_COLORS[5]}>
            <FiDollarSign />
          </CardIcon>
          <CardInfo>
            <CardLabel>Total PO Value</CardLabel>
            <CardValue>{formatCurrency(summary.totalValue, { compact: true })}</CardValue>
          </CardInfo>
        </SummaryCard>
      </CardsGrid>

      {/* Filters */}
      <FiltersCard>
        <FiltersRow>
          <FilterGroup>
            <Label>Financial Year</Label>
            <Select
              value={selectedFYId}
              onChange={(e) => setSelectedFYId(e.target.value)}
            >
              <option value="">-- Select Year --</option>
              {financialYears.map(fy => (
                <option key={fy.id} value={fy.id}>
                  {fy.yearName || fy.yearCode}
                </option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Date From</Label>
            <DateInput
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </FilterGroup>

          <FilterGroup>
            <Label>Date To</Label>
            <DateInput
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </FilterGroup>

          <FilterGroup>
            <Label>Vendor</Label>
            <VendorSelect
              vendors={vendors}
              selectedVendorId={selectedVendor}
              onChange={setSelectedVendor}
              allOption={{ value: 'all', label: 'All Vendors' }}
            />
          </FilterGroup>

          <FilterGroup>
            <Label>Status</Label>
            <Select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
            >
              {STATUS_OPTIONS.map(s => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Fulfillment</Label>
            <Select
              value={selectedFulfillment}
              onChange={(e) => setSelectedFulfillment(e.target.value)}
            >
              {FULFILLMENT_OPTIONS.map(f => (
                <option key={f.value} value={f.value}>{f.label}</option>
              ))}
            </Select>
          </FilterGroup>

          <ButtonGroup>
            <Button onClick={handleResetFilters} title="Reset Filters">
              <FiRotateCcw /> Reset
            </Button>
            <Button onClick={handleExport}>
              <FiDownload /> CSV
            </Button>
            <Button onClick={handlePrint}>
              <FiPrinter /> Print
            </Button>
          </ButtonGroup>
        </FiltersRow>
      </FiltersCard>

      {/* Data Table */}
      <ContentCard>
        <TableHeader>
          <TableHeaderTitle>
            Purchase Order Tracking
            <TableCount>{sortedData.length} PO{sortedData.length !== 1 ? 's' : ''}</TableCount>
          </TableHeaderTitle>
        </TableHeader>
        <TableContainer>
          {loading ? (
            <EmptyState>
              <LoadingSpinner />
              <EmptyText>Loading PO tracking data...</EmptyText>
            </EmptyState>
          ) : sortedData.length === 0 ? (
            <EmptyState>
              <EmptyIcon><FiShoppingCart /></EmptyIcon>
              <EmptyText>
                {poTransactions.length === 0
                  ? 'No purchase orders found for this financial year'
                  : 'No purchase orders match the selected filters'}
              </EmptyText>
              <EmptySubText>Try adjusting your filters or selecting a different financial year</EmptySubText>
            </EmptyState>
          ) : (
            <Table>
              <Thead>
                <tr>
                  <Th style={{ width: 30 }}></Th>
                  <Th sortable onClick={() => handleSort('docDate')}>
                    Date {renderSortIcon('docDate')}
                  </Th>
                  <Th sortable onClick={() => handleSort('docNumber')}>
                    PO # {renderSortIcon('docNumber')}
                  </Th>
                  <Th sortable onClick={() => handleSort('partyName')}>
                    Vendor {renderSortIcon('partyName')}
                  </Th>
                  <Th align="right" sortable onClick={() => handleSort('poTotalQty')}>
                    Ordered Qty {renderSortIcon('poTotalQty')}
                  </Th>
                  <Th align="right" sortable onClick={() => handleSort('poAmount')}>
                    PO Amount {renderSortIcon('poAmount')}
                  </Th>
                  <Th align="center" sortable onClick={() => handleSort('receivingCount')}>
                    Receivings {renderSortIcon('receivingCount')}
                  </Th>
                  <Th align="right" sortable onClick={() => handleSort('receivingTotalQty')}>
                    Received Qty {renderSortIcon('receivingTotalQty')}
                  </Th>
                  <Th align="right" sortable onClick={() => handleSort('receivingTotalAmount')}>
                    Received Amt {renderSortIcon('receivingTotalAmount')}
                  </Th>
                  <Th align="center" sortable onClick={() => handleSort('fulfillmentPct')}>
                    Fulfillment {renderSortIcon('fulfillmentPct')}
                  </Th>
                  <Th sortable onClick={() => handleSort('status')}>
                    Status {renderSortIcon('status')}
                  </Th>
                </tr>
              </Thead>
              <Tbody>
                {sortedData.map((po) => (
                  <React.Fragment key={po.id}>
                    <Tr
                      $clickable
                      onClick={() => toggleRowExpand(po.id)}
                    >
                      <Td style={{ width: 30, paddingRight: 0 }}>
                        <ExpandIcon $expanded={expandedRows[po.id]}>
                          {(po.receivingCount > 0 || (po.itemBreakdown && po.itemBreakdown.length > 0)) ? (
                            expandedRows[po.id] ? <FiChevronDown size={14} /> : <FiChevronRight size={14} />
                          ) : null}
                        </ExpandIcon>
                      </Td>
                      <Td>{formatDate(po.docDate)}</Td>
                      <Td>
                        <DocLink onClick={(e) => { e.stopPropagation(); handleNavigateToPO(po.id); }}>
                          {po.docNumber || '-'}
                        </DocLink>
                        {(po.status !== 'Completed' && po.status !== 'Cancelled' && po.status !== 'Closed' && po.fulfillmentPct < 100) && (
                          <ChildDocBtnGroup onClick={(e) => e.stopPropagation()}>
                            <ChildDocBtn
                              title="Create GRN against this PO"
                              onClick={() => navigate('/purchases/grn', {
                                state: {
                                  prefill: {
                                    parentId: po.id,
                                    parentDocNumber: po.docNumber,
                                    parentTranType: 'PO',
                                    partyId: po.partyId,
                                    partyName: po.partyName,
                                    lines: po.lines || []
                                  }
                                }
                              })}
                            >+ GRN</ChildDocBtn>
                            <ChildDocBtn
                              title="Create Purchase Invoice against this PO"
                              onClick={() => navigate('/purchases/purchase-invoice', {
                                state: {
                                  prefill: {
                                    parentId: po.id,
                                    parentDocNumber: po.docNumber,
                                    parentTranType: 'PO',
                                    partyId: po.partyId,
                                    partyName: po.partyName,
                                    lines: po.lines || []
                                  }
                                }
                              })}
                            >+ PI</ChildDocBtn>
                          </ChildDocBtnGroup>
                        )}
                      </Td>
                      <Td>{po.partyName || '-'}</Td>
                      <AmountTd align="right">
                        {formatNumber(po.poTotalQty)}
                      </AmountTd>
                      <AmountTd align="right" $highlight>
                        {formatAmountLocal(po.poAmount)}
                      </AmountTd>
                      <Td align="center">
                        {po.receivingCount > 0 ? (
                          <CountBadge $type="GRN">{po.receivingCount}</CountBadge>
                        ) : (
                          <ZeroCount>0</ZeroCount>
                        )}
                      </Td>
                      <AmountTd align="right">
                        {formatNumber(po.receivingTotalQty)}
                      </AmountTd>
                      <AmountTd align="right">
                        {formatAmountLocal(po.receivingTotalAmount)}
                      </AmountTd>
                      <Td align="center">
                        <FulfillmentCell>
                          <ProgressBarContainer>
                            <ProgressBarFill
                              $width={Math.min(po.fulfillmentPct, 100)}
                              $color={getFulfillmentColor(po.fulfillmentPct)}
                            />
                          </ProgressBarContainer>
                          <FulfillmentPct $color={getFulfillmentColor(po.fulfillmentPct)}>
                            {(po.fulfillmentPct || 0).toFixed(1)}%
                          </FulfillmentPct>
                        </FulfillmentCell>
                      </Td>
                      <Td>
                        <StatusBadge $status={po.status}>
                          {po.status || 'Unknown'}
                        </StatusBadge>
                      </Td>
                    </Tr>

                    {/* Expandable Row Detail */}
                    {expandedRows[po.id] && (
                      <ExpandedRow>
                        <td colSpan={11}>
                          <ExpandedContent>
                            {/* Item-wise Breakdown */}
                            {po.itemBreakdown && po.itemBreakdown.length > 0 && (
                              <LinkedDocSection>
                                <LinkedDocTitle>
                                  <FiPackage size={14} />
                                  Item-wise Tracking
                                </LinkedDocTitle>
                                <LinkedDocTable>
                                  <thead>
                                    <tr>
                                      <LinkedDocTh>Item Code</LinkedDocTh>
                                      <LinkedDocTh>Item Name</LinkedDocTh>
                                      <LinkedDocTh>UOM</LinkedDocTh>
                                      <LinkedDocTh align="right">Ordered</LinkedDocTh>
                                      <LinkedDocTh align="right">Received</LinkedDocTh>
                                      <LinkedDocTh align="right">Pending</LinkedDocTh>
                                      <LinkedDocTh align="center">Received %</LinkedDocTh>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {po.itemBreakdown.map((item, idx) => (
                                      <tr key={item.itemId || idx}>
                                        <LinkedDocTd style={{ fontWeight: 500 }}>{item.itemCode || '-'}</LinkedDocTd>
                                        <LinkedDocTd>{item.itemName || '-'}</LinkedDocTd>
                                        <LinkedDocTd>{item.uom || '-'}</LinkedDocTd>
                                        <LinkedDocTd align="right" style={{ fontFamily: "'SF Mono','Consolas',monospace", fontSize: 12 }}>
                                          {formatNumber(item.orderedQty)}
                                        </LinkedDocTd>
                                        <LinkedDocTd align="right" style={{ fontFamily: "'SF Mono','Consolas',monospace", fontSize: 12, color: item.receivedQty > 0 ? '#28A745' : undefined }}>
                                          {formatNumber(item.receivedQty)}
                                        </LinkedDocTd>
                                        <LinkedDocTd align="right" style={{ fontFamily: "'SF Mono','Consolas',monospace", fontSize: 12, color: item.pendingQty > 0 ? '#DC3545' : item.closedQty > 0 ? '#546E7A' : '#28A745' }}>
                                          {item.closedQty > 0 ? `${formatNumber(item.closedQty)} closed` : formatNumber(item.pendingQty)}
                                        </LinkedDocTd>
                                        <LinkedDocTd align="center">
                                          <FulfillmentCell>
                                            <ProgressBarContainer style={{ width: 50 }}>
                                              <ProgressBarFill
                                                $width={Math.min(item.receivedPct, 100)}
                                                $color={getFulfillmentColor(item.receivedPct)}
                                              />
                                            </ProgressBarContainer>
                                            <FulfillmentPct $color={getFulfillmentColor(item.receivedPct)}>
                                              {item.receivedPct.toFixed(0)}%
                                            </FulfillmentPct>
                                          </FulfillmentCell>
                                        </LinkedDocTd>
                                      </tr>
                                    ))}
                                  </tbody>
                                </LinkedDocTable>
                              </LinkedDocSection>
                            )}

                            {/* Combined Receivings (GRN + PI) */}
                            {po.receivings.length > 0 && (
                              <LinkedDocSection>
                                <LinkedDocTitle>
                                  <FiFileText size={14} />
                                  Receivings ({po.receivingCount})
                                </LinkedDocTitle>
                                <LinkedDocTable>
                                  <thead>
                                    <tr>
                                      <LinkedDocTh>Type</LinkedDocTh>
                                      <LinkedDocTh>Doc #</LinkedDocTh>
                                      <LinkedDocTh>Date</LinkedDocTh>
                                      <LinkedDocTh align="right">Qty</LinkedDocTh>
                                      <LinkedDocTh align="right">Amount</LinkedDocTh>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {po.receivings.map(rec => (
                                      <tr key={rec.id}>
                                        <LinkedDocTd>
                                          <CountBadge $type={rec.type}>{rec.type}</CountBadge>
                                        </LinkedDocTd>
                                        <LinkedDocTd>
                                          <LinkedDocLink
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              navigateToTransaction(navigate, rec.type, rec.id);
                                            }}
                                            title={`Open ${rec.type} ${rec.docNumber || ''}`}
                                          >
                                            {rec.docNumber || '-'} <FiExternalLink size={10} />
                                          </LinkedDocLink>
                                        </LinkedDocTd>
                                        <LinkedDocTd>{formatDate(rec.docDate)}</LinkedDocTd>
                                        <LinkedDocTd align="right" style={{ fontFamily: "'SF Mono','Consolas',monospace", fontSize: 12 }}>
                                          {formatNumber(rec.qty)}
                                        </LinkedDocTd>
                                        <LinkedDocTd align="right" style={{ fontFamily: "'SF Mono','Consolas',monospace", fontSize: 12 }}>
                                          {formatAmountLocal(rec.amount)}
                                        </LinkedDocTd>
                                      </tr>
                                    ))}
                                  </tbody>
                                </LinkedDocTable>
                              </LinkedDocSection>
                            )}
                          </ExpandedContent>
                        </td>
                      </ExpandedRow>
                    )}
                  </React.Fragment>
                ))}

                {/* Totals Row */}
                <TotalsRow>
                  <Td />
                  <Td colSpan={2} style={{ textAlign: 'right', fontWeight: 700 }}>
                    TOTAL ({sortedData.length} POs)
                  </Td>
                  <Td />
                  <AmountTd align="right"><strong>{formatNumber(tableTotals.poTotalQty)}</strong></AmountTd>
                  <AmountTd align="right" $highlight>
                    {formatAmountLocal(tableTotals.poAmount)}
                  </AmountTd>
                  <Td align="center"><strong>{tableTotals.receivingCount}</strong></Td>
                  <AmountTd align="right"><strong>{formatNumber(tableTotals.receivingTotalQty)}</strong></AmountTd>
                  <AmountTd align="right">
                    {formatAmountLocal(tableTotals.receivingTotalAmount)}
                  </AmountTd>
                  <Td />
                  <Td />
                </TotalsRow>
              </Tbody>
            </Table>
          )}
        </TableContainer>
      </ContentCard>

      {/* Charts */}
      {!loading && filteredData.length > 0 && (
        <ChartsGrid>
          {/* Pie Chart: PO Status Distribution */}
          <ChartCard>
            <ChartTitle>PO Status Distribution</ChartTitle>
            {statusPieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={statusPieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={3}
                    dataKey="value"
                    nameKey="name"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {statusPieData.map((entry, index) => {
                      const order = ['Draft', 'Approved', 'Partially Received', 'Completed', 'Closed', 'Cancelled'];
                      const colorIndex = order.indexOf(entry.name);
                      return (
                        <Cell
                          key={`cell-${index}`}
                          fill={colorIndex >= 0 ? PIE_STATUS_COLORS[colorIndex] : CHART_COLORS[index % CHART_COLORS.length]}
                        />
                      );
                    })}
                  </Pie>
                  <Tooltip content={<PieTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <ChartEmpty>No status data available</ChartEmpty>
            )}
          </ChartCard>

          {/* Bar Chart: PO Value by Vendor (Top 10) */}
          <ChartCard>
            <ChartTitle>PO Value by Vendor (Top 10)</ChartTitle>
            {vendorBarData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={vendorBarData}
                  layout="vertical"
                  margin={{ top: 10, right: 30, left: 10, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#E9ECEF" horizontal={false} />
                  <XAxis
                    type="number"
                    tick={{ fontSize: 11, fill: colors.text.secondary }}
                    axisLine={{ stroke: '#DEE2E6' }}
                    tickLine={false}
                    tickFormatter={(v) => formatCurrency(v, { compact: true })}
                  />
                  <YAxis
                    type="category"
                    dataKey="vendor"
                    tick={{ fontSize: 11, fill: colors.text.secondary }}
                    axisLine={false}
                    tickLine={false}
                    width={120}
                  />
                  <Tooltip content={<CurrencyTooltip />} />
                  <Bar
                    dataKey="value"
                    name="PO Value"
                    fill={CHART_COLORS[0]}
                    radius={[0, 4, 4, 0]}
                    barSize={20}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <ChartEmpty>No vendor data available</ChartEmpty>
            )}
          </ChartCard>

          {/* Line Chart: PO Trend Over Time */}
          <ChartCard $span={2}>
            <ChartTitle>PO Trend Over Time</ChartTitle>
            {trendLineData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={trendLineData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E9ECEF" />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 11, fill: colors.text.secondary }}
                    axisLine={{ stroke: '#DEE2E6' }}
                    tickLine={false}
                  />
                  <YAxis
                    yAxisId="left"
                    tick={{ fontSize: 11, fill: colors.text.secondary }}
                    axisLine={false}
                    tickLine={false}
                    allowDecimals={false}
                  />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    tick={{ fontSize: 11, fill: colors.text.secondary }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(v) => formatCurrency(v, { compact: true })}
                  />
                  <Tooltip content={<TrendTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="count"
                    name="PO Count"
                    stroke={CHART_COLORS[0]}
                    strokeWidth={2.5}
                    dot={{ fill: CHART_COLORS[0], r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="value"
                    name="PO Value"
                    stroke={CHART_COLORS[5]}
                    strokeWidth={2.5}
                    dot={{ fill: CHART_COLORS[5], r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <ChartEmpty>No trend data available</ChartEmpty>
            )}
          </ChartCard>
        </ChartsGrid>
      )}
    </PageLayout>
  );
};

// ══════════════════════════════════════════════════════════════
//  Styled Components
// ══════════════════════════════════════════════════════════════

// ── Summary Cards ──

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
`;

const SummaryCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  transition: box-shadow 0.2s ease, transform 0.2s ease;

  &:hover {
    box-shadow: ${colors.shadows.md};
    transform: translateY(-1px);
  }
`;

const CardIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${colors.borderRadius.md};
  background: ${props => props.color || colors.primary.main}15;
  color: ${props => props.color || colors.primary.main};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
`;

const CardInfo = styled.div`
  min-width: 0;
  flex: 1;
`;

const CardLabel = styled.div`
  font-size: 12px;
  color: ${colors.text.secondary};
  margin-bottom: 4px;
  text-transform: uppercase;
  font-weight: 600;
`;

const CardValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: ${colors.text.primary};
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// ── Filters ──

const FiltersCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
  margin-bottom: 20px;
`;

const FiltersRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  align-items: flex-end;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 150px;
`;

const Label = styled.label`
  font-size: 11px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const Select = styled.select`
  padding: 9px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  color: ${colors.text.primary};
  background: white;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 0;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }
`;

const DateInput = styled.input`
  padding: 9px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  color: ${colors.text.primary};
  background: white;
  transition: all 0.2s;
  min-width: 140px;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  margin-left: auto;
`;

const Button = styled.button`
  padding: 9px 16px;
  border: none;
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;
  background: ${colors.neutral[100]};
  color: ${colors.text.primary};
  white-space: nowrap;

  &:hover {
    background: ${colors.neutral[200]};
  }
`;

// ── Charts ──

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin-bottom: 20px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const ChartCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
  grid-column: ${props => props.$span ? `span ${props.$span}` : 'span 1'};

  @media (max-width: 1024px) {
    grid-column: span 1;
  }
`;

const ChartTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.text.primary};
  margin-bottom: 16px;
`;

const ChartEmpty = styled.div`
  height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.text.tertiary};
  font-size: 14px;
`;

const ChartTooltipWrapper = styled.div`
  background: white;
  border: 1px solid ${colors.neutral[200]};
  border-radius: ${colors.borderRadius.md};
  box-shadow: ${colors.shadows.md};
  padding: 10px 14px;
  font-size: 12px;
`;

const ChartTooltipLabel = styled.div`
  font-weight: 600;
  color: ${colors.text.primary};
  margin-bottom: 6px;
  font-size: 12px;
`;

const ChartTooltipRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: ${colors.text.secondary};
  margin-bottom: 2px;

  strong {
    color: ${colors.text.primary};
    margin-left: auto;
  }
`;

const ChartTooltipDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
`;

// ── Table ──

const ContentCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  overflow: hidden;
  margin-bottom: 20px;
`;

const TableHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid ${colors.neutral[100]};
`;

const TableHeaderTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: ${colors.text.primary};
  display: flex;
  align-items: center;
  gap: 10px;
`;

const TableCount = styled.span`
  font-size: 12px;
  font-weight: 400;
  color: ${colors.text.secondary};
  background: ${colors.neutral[100]};
  padding: 3px 10px;
  border-radius: 12px;
`;

const TableContainer = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Thead = styled.thead`
  background: ${colors.neutral[50]};
`;

const Th = styled.th`
  padding: 11px 14px;
  text-align: ${props => props.align || 'left'};
  font-size: 11px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid ${colors.neutral[200]};
  white-space: nowrap;
  user-select: none;

  ${props => props.sortable && `
    cursor: pointer;
    transition: color 0.2s;

    &:hover {
      color: ${colors.primary.main};
    }
  `}
`;

const Tbody = styled.tbody``;

const Tr = styled.tr`
  border-bottom: 1px solid ${colors.neutral[100]};
  transition: background 0.15s;
  cursor: ${props => props.$clickable ? 'pointer' : 'default'};

  &:hover {
    background: ${colors.neutral[50]};
  }
`;

const TotalsRow = styled.tr`
  background: ${colors.primary.main}08;
  border-top: 2px solid ${colors.primary.main};

  td {
    font-weight: 700 !important;
    font-size: 13px !important;
    padding: 12px 14px !important;
  }
`;

const Td = styled.td`
  padding: 10px 14px;
  font-size: 13px;
  color: ${colors.text.primary};
  text-align: ${props => props.align || 'left'};
  white-space: nowrap;
`;

const AmountTd = styled(Td)`
  font-family: 'SF Mono', 'Consolas', monospace;
  font-size: 12px;
  color: ${props => props.$highlight ? colors.text.primary : colors.text.secondary};
  font-weight: ${props => props.$highlight ? 600 : 400};
`;

// ── Expand / Detail Row ──

const ExpandIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${colors.text.tertiary};
  transition: transform 0.2s;
`;

const ExpandedRow = styled.tr`
  background: ${colors.neutral[50]};
  border-bottom: 1px solid ${colors.neutral[200]};
`;

const ExpandedContent = styled.div`
  padding: 16px 20px 16px 60px;
  display: flex;
  gap: 30px;
  flex-wrap: wrap;
`;

const LinkedDocSection = styled.div`
  min-width: 260px;
  flex: 1;
`;

const LinkedDocTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${colors.text.primary};
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const LinkedDocTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: white;
  border-radius: ${colors.borderRadius.md};
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
`;

const LinkedDocTh = styled.th`
  padding: 8px 12px;
  text-align: ${props => props.align || 'left'};
  font-size: 10px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.4px;
  background: ${colors.neutral[100]};
  border-bottom: 1px solid ${colors.neutral[200]};
`;

const LinkedDocTd = styled.td`
  padding: 7px 12px;
  font-size: 12px;
  color: ${colors.text.primary};
  text-align: ${props => props.align || 'left'};
  border-bottom: 1px solid ${colors.neutral[100]};
`;

const LinkedDocNumber = styled.span`
  font-weight: 600;
  color: ${colors.primary.main};
`;

const LinkedDocLink = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-weight: 600;
  color: ${colors.primary.main};
  cursor: pointer;
  transition: color 0.2s;

  &:hover {
    color: ${colors.primary.light};
    text-decoration: underline;
  }
`;

// ── Doc Link ──

const DocLink = styled.span`
  font-weight: 600;
  color: ${colors.primary.main};
  cursor: pointer;
  transition: color 0.2s;

  &:hover {
    color: ${colors.primary.light};
    text-decoration: underline;
  }
`;

const ChildDocBtnGroup = styled.div`
  display: inline-flex; gap: 4px; margin-left: 8px;
`;
const ChildDocBtn = styled.button`
  font-size: 10px;
  padding: 2px 8px;
  background: ${colors.primary.main}15;
  color: ${colors.primary.main};
  border: 1px solid ${colors.primary.main}40;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 600;
  &:hover { background: ${colors.primary.main}; color: white; }
`;

// ── Status Badge ──

const StatusBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  background: ${props => {
    const c = STATUS_COLORS[props.$status] || colors.neutral[500];
    return `${c}15`;
  }};
  color: ${props => STATUS_COLORS[props.$status] || colors.neutral[600]};
`;

// ── Count Badge ──

const CountBadge = styled.span`
  display: inline-block;
  min-width: 28px;
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  text-align: center;
  background: ${props => {
    switch (props.$type) {
      case 'GRN': return `${CHART_COLORS[1]}12`;
      case 'PI': return `${CHART_COLORS[3]}15`;
      default: return `${colors.neutral[200]}`;
    }
  }};
  color: ${props => {
    switch (props.$type) {
      case 'GRN': return CHART_COLORS[1];
      case 'PI': return CHART_COLORS[4];
      default: return colors.text.secondary;
    }
  }};
`;

const ZeroCount = styled.span`
  color: ${colors.neutral[400]};
  font-size: 12px;
`;

// ── Fulfillment Progress Bar ──

const FulfillmentCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: center;
`;

const ProgressBarContainer = styled.div`
  width: 70px;
  height: 8px;
  background: ${colors.neutral[200]};
  border-radius: 4px;
  overflow: hidden;
  flex-shrink: 0;
`;

const ProgressBarFill = styled.div`
  height: 100%;
  width: ${props => props.$width || 0}%;
  background: ${props => props.$color || colors.success.main};
  border-radius: 4px;
  transition: width 0.3s ease;
`;

const FulfillmentPct = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: ${props => props.$color || colors.text.secondary};
  min-width: 40px;
  text-align: right;
`;

// ── Empty / Loading ──

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${colors.text.secondary};
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  color: ${colors.neutral[300]};
  margin-bottom: 16px;
`;

const EmptyText = styled.p`
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 6px;
  color: ${colors.text.primary};
`;

const EmptySubText = styled.p`
  font-size: 13px;
  color: ${colors.text.tertiary};
`;

const LoadingSpinner = styled.div`
  width: 36px;
  height: 36px;
  border: 3px solid ${colors.neutral[200]};
  border-top: 3px solid ${colors.primary.main};
  border-radius: 50%;
  margin: 0 auto 16px auto;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

export default POTracking;
